import { useIsMutating, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { fetchBillingPurchase, isBillingForbidden } from "@/api/billing";
import {
  type BillingPurchaseSnapshot,
  requiresPurchaseReview,
} from "@/components/billing/billing-attempt";
import { queryKeys } from "@/lib/query-keys";

/** Polls owned purchase reads every two seconds for at most one minute while visible. Manual refresh never creates payment. */
export function useBillingPurchaseReturn(orgId: string, purchaseId: string) {
  const checkoutPending =
    useIsMutating({ mutationKey: queryKeys.billing.checkout(orgId) }) > 0;
  const [visible, setVisible] = useState(
    () => document.visibilityState !== "hidden",
  );
  const [deadline] = useState(() => Date.now() + 60_000);
  const [exhausted, setExhausted] = useState(false);
  useEffect(() => {
    const onVisibility = () =>
      setVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", onVisibility);
    const timer = window.setTimeout(
      () => setExhausted(true),
      Math.max(0, deadline - Date.now()),
    );
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.clearTimeout(timer);
    };
  }, [deadline]);
  const query = useQuery<BillingPurchaseSnapshot>({
    queryKey: queryKeys.billing.purchase(orgId, purchaseId),
    queryFn: () => fetchBillingPurchase(orgId, purchaseId),
    enabled: visible && !exhausted && !checkoutPending,
    retry: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const data = query.state.data;
      const terminal =
        isBillingForbidden(query.state.error) ||
        data?.fulfillment_status === "fulfilled" ||
        (data && requiresPurchaseReview(data)) ||
        data?.payment_status === "failed" ||
        data?.payment_status === "expired";
      return visible &&
        !exhausted &&
        !checkoutPending &&
        Date.now() < deadline &&
        !terminal
        ? 2000
        : false;
    },
  });
  return { ...query, exhausted, checkoutPending };
}
